"use client";

import Link from "next/link";
import { useEffect } from "react";

import { useTranslations } from "next-intl";

import { createLogger } from "@/utils/logger";

const logger = createLogger("error-page");

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
  title?: string;
  description?: string;
  showReset?: boolean;
}

export default function ErrorPage({
  error,
  reset,
  title,
  description,
  showReset = true,
}: ErrorPageProps) {
  const t = useTranslations("common");

  useEffect(() => {
    logger.error("Error Page Encountered:", error);
  }, [error]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-4 py-8">
      <div className="text-center max-w-2xl">
        <h1 className="text-3xl md:text-4xl font-bold text-red-600 mb-4">
          {title || t("something_went_wrong")}
        </h1>

        <p className="text-gray-600 mb-6 text-lg">
          {description || t("error_occurred_please_try_again")}
        </p>

        {process.env.NODE_ENV === "development" && (
          <details className="mb-6 text-left bg-gray-100 p-4 rounded border">
            <summary className="cursor-pointer font-medium text-gray-700 mb-2">
              Development Error Details
            </summary>
            <pre className="text-sm text-gray-600 whitespace-pre-wrap">
              {error.message}
              {error.stack && "\n\nStack Trace:\n" + error.stack}
            </pre>
          </details>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {showReset && (
            <button
              onClick={reset}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
            >
              {t("try_again")}
            </button>
          )}

          <Link
            href="/"
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t("go_home")}
          </Link>
        </div>
      </div>
    </div>
  );
}
